import { Div, Flex } from "@hopper-ui/components";
import {
    ConversationInputForm,
    ConversationStopButton,
    ConversationView,
    FileDropZone,
    Footer,
    InteractiveQuestionCard,
    PageLoading,
    ScrollToBottomButton,
    useConversationIdRouteParams,
    useCreateConversationMessage,
    useFilesDragAndDrop,
    useIsAssistantGenerating,
    useIsCreateConversationMessageMutating,
    usePendingQuestion,
    useUploadFiles
} from "@workleap-ai/shared";
import { Suspense, useCallback, useRef } from "react";

const ConversationPage = () => {
    const { conversationId } = useConversationIdRouteParams();

    return (
        <Suspense fallback={<PageLoading />}>
            <ConversationPageChild conversationId={conversationId} />
        </Suspense>
    );
};

interface Props {
    conversationId: string;
}

export const ConversationPageChild = ({ conversationId }: Props) => {
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const isMutating = useIsCreateConversationMessageMutating(conversationId);
    const isAssistantGenerating = useIsAssistantGenerating(conversationId);
    const { handleFileSelected, files, removeFile, removeAllFiles } = useUploadFiles(conversationId);
    const {
        isDropZoneActive,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useFilesDragAndDrop(handleFileSelected, isMutating);

    const { createConversationMessage } = useCreateConversationMessage();

    const onSubmit = useCallback((value: string) => {
        createConversationMessage(conversationId, value, { files });
        removeAllFiles();
    }, [createConversationMessage, conversationId, files, removeAllFiles]);

    const pendingQuestion = usePendingQuestion(conversationId);

    return (
        <Flex
            direction="column"
            height="100%"
            position="relative"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <ConversationView
                scrollableContainerRef={scrollableContainerRef}
                conversationId={conversationId}
            />
            <Div position="relative">
                <ScrollToBottomButton scrollableContainerRef={scrollableContainerRef} isHidden={!!pendingQuestion} />
            </Div>
            <Div paddingRight="inset-sm">
                <Div
                    paddingX="inset-lg"
                    paddingTop="inset-lg"
                    paddingBottom={{
                        base: "core_0",
                        sm: "inset-lg"
                    }}
                    UNSAFE_maxWidth="var(--wai-page-max-width)"
                    marginX="auto"
                    width="100%"
                >
                    {pendingQuestion ? (
                        <InteractiveQuestionCard
                            key={pendingQuestion.interruptId}
                            questions={pendingQuestion.questions}
                            promptFeature={pendingQuestion.promptFeature}
                            onAnswer={pendingQuestion.answer}
                            onClose={pendingQuestion.dismiss}
                        />
                    ) : (
                        <ConversationInputForm
                            conversationId={conversationId}
                            files={files}
                            isMutating={isMutating}
                            isAssistantGenerating={isAssistantGenerating}
                            autofocus
                            padding="inset-md"
                            stopButton={<ConversationStopButton conversationId={conversationId} />}
                            handleFileSelected={handleFileSelected}
                            removeFile={removeFile}
                            onSubmit={onSubmit}
                        />
                    )}
                    <Footer />
                </Div>
            </Div>
            {isDropZoneActive && <FileDropZone />}
        </Flex>
    );
};

export default ConversationPage;
